import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import axios from 'axios'
import { BaseHook, BaseNotification, BaseRequestConfig, BaseRequestOptions } from '@/types'
import { baseVuexStore } from '@/plugins/vuex-store'
import { createHooks } from '@/utils/hook-util'
import appManifest from '~/public/manifest.json'

enum StorageKey {
  ROUTE_PATH = 'app.routePath',
  LANGUAGE = 'app.language',
  THEME_DARK = 'app.themeDark',
  NOTIFICATIONS = 'app.notifications',
  SERVER_VERSION = 'app.serverVersion'
}

const APP_STORE_DEFAULT_SUPPORTED_LANGUAGE = appManifest.supported_languages
  .find((supportedLanguage) => supportedLanguage.default)
  ?.code ?? ''
const APP_STORE_SUPPORTED_LANGUAGES: ReadonlyArray<string> = appManifest.supported_languages
  .map((supportedLanguage) => supportedLanguage.code)
const APP_STORE_NAVIGATOR_LANGUAGES: ReadonlyArray<string> = ([] as string[])
  .concat(navigator.language, navigator.languages)
  .filter((navigatorLanguage) => navigatorLanguage)

const routePathHooks = createHooks<string>()
const languageHooks = createHooks<string>()
const themeDarkHooks = createHooks<boolean>()
const notificationsHooks = createHooks<BaseNotification[]>()

class RequestOptions<T> implements BaseRequestOptions<T> {
  static readonly SERVER_VERSION = new RequestOptions<string>('/version', {
    authorized: true,
    headers: {
      Accept: 'text/plain'
    }
  })

  readonly requestPath: string
  readonly requestConfig?: BaseRequestConfig<T>

  private constructor(requestPath: string, requestConfig?: BaseRequestConfig<T>) {
    this.requestPath = requestPath
    this.requestConfig = requestConfig
  }
}

function normalizeRoutePath(routePath?: string | null): string {
  return routePath ?? '/'
}

function normalizeLanguage(language?: string | null): string {
  const userLanguages = language
    ? ([] as string[]).concat(language, APP_STORE_NAVIGATOR_LANGUAGES)
    : APP_STORE_NAVIGATOR_LANGUAGES

  language = null

  for (let userLanguage of userLanguages) {
    language = APP_STORE_SUPPORTED_LANGUAGES.find((supportedLanguage) => supportedLanguage === userLanguage)
    if (language) break

    userLanguage = userLanguage.split('-')[0]

    language = APP_STORE_SUPPORTED_LANGUAGES.find((supportedLanguage) => supportedLanguage.startsWith(userLanguage))
    if (language) break
  }

  return language ?? APP_STORE_DEFAULT_SUPPORTED_LANGUAGE
}

function normalizeThemeDark(themeDark?: boolean | null): boolean {
  return themeDark ?? false
}

function normalizeNotifications(notifications?: BaseNotification[] | null): BaseNotification[] {
  return notifications ?? []
}

function normalizeServerVersion(serverVersion?: string | null): string {
  return serverVersion ?? ''
}

function normalizeDownloadingCrud(downloadingCrud?: boolean | null): boolean {
  return downloadingCrud ?? false
}

@Module({ name: 'app', namespaced: true, store: baseVuexStore })
export default class AppStore extends VuexModule {
  routePath: string = normalizeRoutePath()
  language: string = normalizeLanguage()
  themeDark: boolean = normalizeThemeDark()
  notifications: BaseNotification[] = normalizeNotifications()
  serverVersion: string = normalizeServerVersion()
  downloadingCrud: boolean = normalizeDownloadingCrud()

  @Mutation
  private loadAll(): void {
    const rootRoutePath = (location.hash.replace('#', '') === '/')

    if (rootRoutePath) this.routePath = normalizeRoutePath(localStorage.getItem(StorageKey.ROUTE_PATH))
    this.language = normalizeLanguage(localStorage.getItem(StorageKey.LANGUAGE))
    this.themeDark = normalizeThemeDark(JSON.parse(localStorage.getItem(StorageKey.THEME_DARK) as string))
    this.notifications = normalizeNotifications(JSON.parse(localStorage.getItem(StorageKey.NOTIFICATIONS) as string))
    this.serverVersion = normalizeServerVersion(localStorage.getItem(StorageKey.SERVER_VERSION))

    if (rootRoutePath) routePathHooks.execute(this.routePath)
    languageHooks.execute(this.language)
    themeDarkHooks.execute(this.themeDark)
    notificationsHooks.execute(this.notifications)
  }

  @Mutation
  setRoutePath(routePath?: string): void {
    const normalizedRoutePath = normalizeRoutePath(routePath)

    if (normalizedRoutePath === this.routePath) return

    if (routePath) localStorage.setItem(StorageKey.ROUTE_PATH, routePath)
    else localStorage.removeItem(StorageKey.ROUTE_PATH)

    this.routePath = normalizedRoutePath

    routePathHooks.execute(this.routePath)
  }

  @Mutation
  setLanguage(language?: string): void {
    const normalizedLanguage = normalizeLanguage(language)

    if (normalizedLanguage === this.language) return

    if (language) localStorage.setItem(StorageKey.LANGUAGE, language)
    else localStorage.removeItem(StorageKey.LANGUAGE)

    this.language = normalizedLanguage

    languageHooks.execute(this.language)
  }

  @Mutation
  setThemeDark(themeDark?: boolean): void {
    const normalizedThemeDark = normalizeThemeDark(themeDark)

    if (normalizedThemeDark === this.themeDark) return

    if (themeDark) localStorage.setItem(StorageKey.THEME_DARK, JSON.stringify(themeDark))
    else localStorage.removeItem(StorageKey.THEME_DARK)

    this.themeDark = normalizedThemeDark

    themeDarkHooks.execute(this.themeDark)
  }

  @Mutation
  private setNotifications(notifications?: BaseNotification[]): void {
    const normalizedNotifications = normalizeNotifications(notifications)

    if (JSON.stringify(normalizedNotifications) === JSON.stringify(this.notifications)) return

    const storageNotifications = notifications?.filter((notification) => !notification.temporary)

    if (storageNotifications?.length) localStorage.setItem(StorageKey.NOTIFICATIONS, JSON.stringify(storageNotifications))
    else localStorage.removeItem(StorageKey.NOTIFICATIONS)

    this.notifications = normalizedNotifications

    notificationsHooks.execute(this.notifications)
  }

  @Mutation
  private setServerVersion(serverVersion?: string): void {
    const normalizedServerVersion = normalizeServerVersion(serverVersion)

    if (normalizedServerVersion === this.serverVersion) return

    if (serverVersion) localStorage.setItem(StorageKey.SERVER_VERSION, serverVersion)
    else localStorage.removeItem(StorageKey.SERVER_VERSION)

    this.serverVersion = normalizedServerVersion
  }

  @Mutation
  setDownloadingCrud(downloadingCrud?: boolean): void {
    this.downloadingCrud = normalizeDownloadingCrud(downloadingCrud)
  }

  @Action({ rawError: true })
  async addNotification(notification?: BaseNotification): Promise<void> {
    if (!notification) return

    let { clickAction, clickTitle, uniqueKey, datetime } = notification

    if (uniqueKey && this.notifications.some((someNotification) => someNotification.uniqueKey === uniqueKey)) return

    if (clickAction && (!clickTitle)) clickTitle = 'app.button.ok'
    if (!datetime) datetime = new Date().toISOString()

    notification.id = (this.notifications[0]?.id ?? 0) + 1
    if (clickTitle) notification.clickTitle = clickTitle
    if (datetime) notification.datetime = datetime

    const notifications = ([] as BaseNotification[]).concat(notification, this.notifications)

    this.setNotifications(notifications)
  }

  @Action({ rawError: true })
  async removeNotification(notification?: BaseNotification): Promise<void> {
    if (!notification) return

    const notificationIndex = this.notifications.indexOf(notification)

    if (notificationIndex < 0) return

    const notifications = ([] as BaseNotification[]).concat(this.notifications)
    notifications.splice(notificationIndex, 1)

    this.setNotifications(notifications)
  }

  @Action({ rawError: true })
  async clearNotifications(): Promise<void> {
    this.setNotifications()
  }

  @Action({ rawError: true })
  private async fetchServerVersion(): Promise<void> {
    const { requestPath, requestConfig } = RequestOptions.SERVER_VERSION

    const { data } = await axios.get<string>(requestPath, requestConfig)

    this.setServerVersion(data)
  }

  @Action({ rawError: true })
  async registerRoutePathHook(routePathHook?: BaseHook<string>): Promise<void> {
    if (routePathHook) routePathHooks.register(routePathHook)
  }

  @Action({ rawError: true })
  async registerLanguageHook(languageHook?: BaseHook<string>): Promise<void> {
    if (languageHook) languageHooks.register(languageHook)
  }

  @Action({ rawError: true })
  async registerThemeDarkHook(themeDarkHook?: BaseHook<boolean>): Promise<void> {
    if (themeDarkHook) themeDarkHooks.register(themeDarkHook)
  }

  @Action({ rawError: true })
  async registerNotificationsHook(notificationsHook?: BaseHook<BaseNotification[]>): Promise<void> {
    if (notificationsHook) notificationsHooks.register(notificationsHook)
  }

  @Action({ rawError: true })
  async start(): Promise<void> {
    this.loadAll()

    try {
      await this.fetchServerVersion()
    }
    catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error during fetch server version:', error)
    }
  }
}

export const appStore = getModule(AppStore)